import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import LocationAutocomplete from '../components/LocationAutocomplete';
import { 
  ArrowLeft, 
  CalendarIcon, 
  MapPin, 
  Image as ImageIcon,
  Plus,
  Trash2,
  Gavel,
  Upload,
  X,
  Save,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const categories = [
  { id: 'gaming', name: 'Gaming' },
  { id: 'beauty', name: 'Beauty & Fashion' },
  { id: 'music', name: 'Music & Concerts' },
  { id: 'comedy', name: 'Comedy' },
  { id: 'fitness', name: 'Fitness & Sports' },
  { id: 'food', name: 'Food & Cooking' },
  { id: 'tech', name: 'Tech & Business' },
  { id: 'lifestyle', name: 'Lifestyle' }
];

export default function EditEvent() {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const { user, isAuthenticated, isInfluencer, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [originalEvent, setOriginalEvent] = useState(null);
  const [ticketsSold, setTicketsSold] = useState(0);
  
  const [eventData, setEventData] = useState({
    title: '',
    description: '',
    category: '',
    event_type: 'in-person',
    location: '',
    location_coords: null,
    location_mode: 'specific',
    event_date: null,
    date_mode: 'specific',
    date_flexible: '',
    time: '',
    image_url: ''
  });
  
  const [ticketTiers, setTicketTiers] = useState([]);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
      return;
    }
    
    if (!authLoading && isAuthenticated) {
      fetchEvent();
    }
  }, [authLoading, isAuthenticated, eventId]);

  const fetchEvent = async () => {
    try {
      const res = await axios.get(`${API_URL}/events/${eventId}`);
      const event = res.data;
      
      // Check ownership
      if (event.influencer_id !== user?.id && user?.role !== 'admin') {
        toast.error('You do not have permission to edit this event');
        navigate('/dashboard');
        return;
      }
      
      setOriginalEvent(event);
      
      // Parse date
      let eventDate = null;
      let dateMode = 'specific';
      let dateFlexible = '';
      
      if (event.event_date) {
        eventDate = parseISO(event.event_date);
      }
      if (event.date_display) {
        dateMode = 'flexible';
        dateFlexible = event.date_display;
      }
      
      setEventData({
        title: event.title || '',
        description: event.description || '',
        category: event.category || '',
        event_type: event.event_type || 'in-person',
        location: event.location || '',
        location_coords: event.location_coords || null,
        location_mode: event.location_mode || 'specific',
        event_date: eventDate,
        date_mode: dateMode,
        date_flexible: dateFlexible,
        time: event.time || '',
        image_url: event.image_url || ''
      });
      
      setImagePreview(event.image_url || null);
      setTicketTiers(event.ticket_tiers || []);
      
      // Get ticket count
      const ticketsRes = await axios.get(`${API_URL}/events/${eventId}/tickets/count`).catch(() => ({ data: { count: 0 } }));
      setTicketsSold(ticketsRes.data?.count || event.sold_tickets || 0);
      
    } catch (error) {
      console.error('Error fetching event:', error);
      toast.error('Failed to load event');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async () => {
    if (!imageFile) return eventData.image_url;
    
    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      
      const res = await axios.post(`${API_URL}/events/${eventId}/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      return res.data.url;
    } catch (error) {
      console.error('Image upload failed:', error);
      toast.error('Failed to upload image');
      return eventData.image_url;
    } finally {
      setUploadingImage(false);
    }
  };

  const updateTicketTier = (index, field, value) => {
    const updated = [...ticketTiers];
    updated[index][field] = value;
    setTicketTiers(updated);
  };

  const addTicketTier = () => {
    setTicketTiers([
      ...ticketTiers,
      {
        name: '',
        description: '',
        price: 0,
        quantity: 50,
        sale_mode: 'fixed'
      }
    ]);
  };

  const removeTicketTier = (index) => {
    if (ticketTiers.length <= 1) {
      toast.error('Must have at least one ticket tier');
      return;
    }
    setTicketTiers(ticketTiers.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    // Validate
    if (!eventData.title.trim()) {
      toast.error('Event title is required');
      return;
    }
    
    if (!eventData.description.trim()) {
      toast.error('Event description is required');
      return;
    }
    
    if (ticketTiers.length === 0) {
      toast.error('At least one ticket tier is required');
      return;
    }
    
    for (const tier of ticketTiers) {
      if (!tier.name.trim()) {
        toast.error('All ticket tiers must have a name');
        return;
      }
      if (tier.price < 0) {
        toast.error('Ticket price cannot be negative');
        return;
      }
      if (tier.quantity < 1) {
        toast.error('Ticket quantity must be at least 1');
        return;
      }
    }
    
    setSaving(true);
    try {
      // Upload new image if selected
      let imageUrl = eventData.image_url;
      if (imageFile) {
        imageUrl = await uploadImage();
      }
      
      // Build update payload
      const updateData = {
        title: eventData.title,
        description: eventData.description,
        category: eventData.category,
        event_type: eventData.event_type,
        location: eventData.location,
        location_mode: eventData.location_mode,
        time: eventData.time,
        image_url: imageUrl,
        ticket_tiers: ticketTiers.map((tier, index) => ({
          ...tier,
          id: tier.id || `tier-${index}`
        }))
      };
      
      // Add date info based on mode
      if (eventData.date_mode === 'specific' && eventData.event_date) {
        updateData.event_date = eventData.event_date.toISOString();
        updateData.date_display = null;
        updateData.date_mode = 'specific';
      } else if (eventData.date_mode === 'flexible' && eventData.date_flexible) {
        updateData.event_date = null;
        updateData.date_display = eventData.date_flexible;
        updateData.date_mode = 'flexible';
      }
      
      await axios.put(`${API_URL}/events/${eventId}`, updateData);
      
      toast.success('Event updated successfully!');
      navigate('/dashboard');
      
    } catch (error) {
      console.error('Error updating event:', error);
      toast.error(error.response?.data?.detail || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/events/${eventId}`);
      toast.success('Event deleted');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error deleting event:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
          <div className="flex items-center gap-2">
            {ticketsSold > 0 && (
              <Badge variant="outline" className="text-amber-400 border-amber-400/30">
                {ticketsSold} tickets sold
              </Badge>
            )}
            <Badge variant={originalEvent?.status === 'published' ? 'default' : 'secondary'}>
              {originalEvent?.status || 'draft'}
            </Badge>
          </div>
        </div>

        <h1 className="font-heading font-bold text-3xl mb-2">Edit Event</h1>
        <p className="text-muted-foreground mb-8">Update your event details</p>

        <div className="space-y-8">
          {/* Basic Info */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label>Event Title *</Label>
                <Input
                  value={eventData.title}
                  onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                  placeholder="Enter event title"
                  className="mt-1"
                  data-testid="edit-event-title"
                />
              </div>

              <div>
                <Label>Description *</Label>
                <Textarea
                  value={eventData.description}
                  onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                  placeholder="Describe your event..."
                  rows={4}
                  className="mt-1"
                  data-testid="edit-event-description"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Category</Label>
                  <Select value={eventData.category} onValueChange={(v) => setEventData({ ...eventData, category: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Event Type</Label>
                  <Select value={eventData.event_type} onValueChange={(v) => setEventData({ ...eventData, event_type: v })}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="in-person">In-Person</SelectItem>
                      <SelectItem value="virtual">Virtual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Date & Location */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle>Date & Location</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {/* Date Section */}
                <div className="space-y-3">
                  <Label>Event Date</Label>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={eventData.date_mode === 'specific' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEventData({ ...eventData, date_mode: 'specific' })}
                        className="flex-1"
                      >
                        Specific Date
                      </Button>
                      <Button
                        type="button"
                        variant={eventData.date_mode === 'flexible' ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => setEventData({ ...eventData, date_mode: 'flexible' })}
                        className="flex-1"
                      >
                        Flexible
                      </Button>
                    </div>
                    
                    {eventData.date_mode === 'specific' ? (
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full justify-start text-left font-normal",
                              !eventData.event_date && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {eventData.event_date ? format(eventData.event_date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={eventData.event_date}
                            onSelect={(date) => setEventData({ ...eventData, event_date: date })}
                            disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                            defaultMonth={eventData.event_date || new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    ) : (
                      <Select 
                        value={eventData.date_flexible} 
                        onValueChange={(v) => setEventData({ ...eventData, date_flexible: v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select timeframe" />
                        </SelectTrigger>
                        <SelectContent>
                          {(() => {
                            const now = new Date();
                            const currentYear = now.getFullYear();
                            const currentMonth = now.getMonth();
                            const currentQuarter = Math.floor(currentMonth / 3) + 1;
                            const nextYear = currentYear + 1;
                            
                            const options = [];
                            
                            for (let q = currentQuarter; q <= 4; q++) {
                              const months = q === 1 ? 'Jan-Mar' : q === 2 ? 'Apr-Jun' : q === 3 ? 'Jul-Sep' : 'Oct-Dec';
                              options.push({ value: `Q${q} ${currentYear}`, label: `Q${q} ${currentYear} (${months})` });
                            }
                            
                            for (let q = 1; q <= 4; q++) {
                              const months = q === 1 ? 'Jan-Mar' : q === 2 ? 'Apr-Jun' : q === 3 ? 'Jul-Sep' : 'Oct-Dec';
                              options.push({ value: `Q${q} ${nextYear}`, label: `Q${q} ${nextYear} (${months})` });
                            }
                            
                            const seasons = ['Spring', 'Summer', 'Fall', 'Winter'];
                            const currentSeasonIndex = Math.floor((currentMonth + 1) / 3) % 4;
                            
                            for (let i = currentSeasonIndex; i < 4; i++) {
                              options.push({ value: `${seasons[i]} ${currentYear}`, label: `${seasons[i]} ${currentYear}` });
                            }
                            for (let i = 0; i < 4; i++) {
                              options.push({ value: `${seasons[i]} ${nextYear}`, label: `${seasons[i]} ${nextYear}` });
                            }
                            
                            options.push({ value: `${currentYear}`, label: `Sometime in ${currentYear}` });
                            options.push({ value: `${nextYear}`, label: `Sometime in ${nextYear}` });
                            options.push({ value: 'TBD', label: 'To Be Announced' });
                            
                            return options.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ));
                          })()}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>

                {/* Time */}
                <div className="space-y-3">
                  <Label>Time</Label>
                  <Input
                    type="time"
                    value={eventData.time}
                    onChange={(e) => setEventData({ ...eventData, time: e.target.value })}
                  />
                </div>
              </div>

              {/* Location */}
              <div className="space-y-3">
                <Label>Location</Label>
                <Select 
                  value={eventData.location_mode} 
                  onValueChange={(v) => setEventData({ ...eventData, location_mode: v, location: '' })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="specific">Specific Location</SelectItem>
                    <SelectItem value="after_sales">Decide After Ticket Sales</SelectItem>
                    <SelectItem value="tbd">To Be Announced</SelectItem>
                  </SelectContent>
                </Select>
                
                {eventData.location_mode === 'specific' && (
                  <LocationAutocomplete
                    value={eventData.location}
                    onChange={(location) => setEventData({ ...eventData, location })}
                    onSelect={(location, coords) => setEventData({ ...eventData, location, location_coords: coords })}
                    placeholder="Search for a city or venue..."
                  />
                )}
              </div>
            </CardContent>
          </Card>

          {/* Event Image */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <CardTitle>Event Image</CardTitle>
            </CardHeader>
            <CardContent>
              <div 
                className="border-2 border-dashed border-white/20 rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {imagePreview ? (
                  <div className="relative">
                    <img src={imagePreview} alt="Event" className="max-h-64 mx-auto rounded-lg" />
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-2 right-2"
                      onClick={(e) => {
                        e.stopPropagation();
                        setImageFile(null);
                        setImagePreview(eventData.image_url || null);
                      }}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="font-medium mb-1">Click to upload event image</p>
                    <p className="text-sm text-muted-foreground">JPEG, PNG, WebP or GIF (max 5MB)</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
              </div>
            </CardContent>
          </Card>

          {/* Ticket Tiers */}
          <Card className="bg-card border-white/10">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Ticket Tiers</CardTitle>
                {ticketsSold > 0 && (
                  <Badge variant="outline" className="text-amber-400 border-amber-400/30">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Limited editing - tickets sold
                  </Badge>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {ticketTiers.map((tier, index) => (
                <div key={index} className="border border-white/10 rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium">Tier {index + 1}</h4>
                    <div className="flex items-center gap-2">
                      {tier.sale_mode === 'auction' && (
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30 gap-1">
                          <Gavel className="w-3 h-3" />
                          Auction
                        </Badge>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeTicketTier(index)}
                        disabled={ticketTiers.length <= 1}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>Tier Name *</Label>
                      <Input
                        value={tier.name}
                        onChange={(e) => updateTicketTier(index, 'name', e.target.value)}
                        placeholder="e.g., General Admission"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={tier.description || ''}
                        onChange={(e) => updateTicketTier(index, 'description', e.target.value)}
                        placeholder="What's included"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label>{tier.sale_mode === 'auction' ? 'Min Bid ($)' : 'Price ($)'}</Label>
                      <Input
                        type="number"
                        value={tier.sale_mode === 'auction' ? (tier.min_bid || 0) : tier.price}
                        onChange={(e) => {
                          if (tier.sale_mode === 'auction') {
                            updateTicketTier(index, 'min_bid', parseFloat(e.target.value) || 0);
                          } else {
                            updateTicketTier(index, 'price', parseFloat(e.target.value) || 0);
                          }
                        }}
                        min="0"
                        step="0.01"
                        className="mt-1"
                        disabled={ticketsSold > 0 && tier.sold > 0}
                      />
                      {ticketsSold > 0 && tier.sold > 0 && (
                        <p className="text-xs text-amber-400 mt-1">Cannot change - tickets sold</p>
                      )}
                    </div>
                    <div>
                      <Label>Quantity</Label>
                      <Input
                        type="number"
                        value={tier.quantity}
                        onChange={(e) => updateTicketTier(index, 'quantity', parseInt(e.target.value) || 1)}
                        min={tier.sold || 1}
                        className="mt-1"
                      />
                      {tier.sold > 0 && (
                        <p className="text-xs text-muted-foreground mt-1">{tier.sold} sold</p>
                      )}
                    </div>
                    <div>
                      <Label>Sale Mode</Label>
                      <Select
                        value={tier.sale_mode || 'fixed'}
                        onValueChange={(v) => updateTicketTier(index, 'sale_mode', v)}
                        disabled={ticketsSold > 0 && tier.sold > 0}
                      >
                        <SelectTrigger className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fixed">Fixed Price</SelectItem>
                          <SelectItem value="auction">Auction</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              ))}
              
              <Button variant="outline" onClick={addTicketTier} className="w-full">
                <Plus className="w-4 h-4 mr-2" />
                Add Ticket Tier
              </Button>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4">
            <Button
              variant="destructive"
              onClick={() => setShowDeleteModal(true)}
              disabled={ticketsSold > 0}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete Event
            </Button>
            
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving || uploadingImage}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </div>
          
          {ticketsSold > 0 && (
            <p className="text-sm text-amber-400 text-center">
              <AlertTriangle className="w-4 h-4 inline mr-1" />
              Some options are limited because tickets have been sold
            </p>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{eventData.title}"? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete Event'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
